import type ImageWrapper from 'ol/Image'
import { FAILED_IMAGE_SRC, geoserverFetch } from 'common/geoserver'

/**
 * Client-side ceiling on a single GetMap. GeoServer's own "max rendering time" is what
 * actually stops the server working; this only stops the browser waiting, and frees the
 * connection for the request the user is currently looking at.
 */
export const REQUEST_TIMEOUT_MS = 25_000

/**
 * 1x1 transparent GIF. A cancelled request settles its <img> on this, as a successful,
 * empty load rather than an error.
 */
export const BLANK_IMAGE_SRC =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

/**
 * Build an `imageLoadFunction` that cancels the request it supersedes and gives up after
 * REQUEST_TIMEOUT_MS.
 *
 * ol 10.10 has no AbortController anywhere in `ol/Image.js` - the default loader is
 * `image.getImage().src = src`, which it never cancels. Panning therefore stacks up
 * concurrent full-extent GetMaps against the browser's six-connection-per-host limit, so
 * the request for the view the user is actually waiting on queues behind stale ones.
 *
 * Requests go through geoserverFetch, which carries the Authorization header GeoServer
 * requires (a plain `img.src` load cannot send one), and are cancelled through an
 * AbortController. A superseded or timed-out request settles its element on a blank GIF,
 * which ol takes as an empty success and then discards, because a newer image has
 * replaced it.
 *
 * The contract to honour: ImageWMS calls this and then immediately calls `decode(image)`.
 * With no `src` set yet, that falls through to `load(image)`, which attaches `load`/`error`
 * listeners and returns a promise that stays pending until one of them fires. So every
 * path out of here must end with a `src` assigned, or the layer sits in LOADING forever.
 *
 * The returned function also carries a `cancel` method. The pending request it tracks lives
 * in this closure, not on the source or layer, so removing/recreating those does nothing to
 * it - the caller must call `cancel()` on teardown, or the request rides out to
 * REQUEST_TIMEOUT_MS regardless of whether anything on screen still wants it.
 */
export type AbortingImageLoader = ((
  image: ImageWrapper,
  src: string,
) => void) & {
  cancel: () => void
}

type PendingRequest = {
  controller: AbortController
  timer: ReturnType<typeof setTimeout>
}

export function createAbortingImageLoader(label: string): AbortingImageLoader {
  let pending: PendingRequest | null = null

  const abortPending = () => {
    if (pending === null) return
    clearTimeout(pending.timer)
    pending.controller.abort()
    pending = null
  }

  const load = async (
    element: HTMLImageElement,
    src: string,
    signal: AbortSignal,
  ) => {
    try {
      const response = await geoserverFetch(src, { signal })
      if (!response.ok) {
        throw new Error(`GeoServer responded with ${response.status}`)
      }
      const objectUrl = URL.createObjectURL(await response.blob())
      element.addEventListener('load', () => URL.revokeObjectURL(objectUrl), {
        once: true,
      })
      element.addEventListener(
        'error',
        () => {
          URL.revokeObjectURL(objectUrl)
          // A ServiceException from GeoServer arrives as HTTP 200 with an XML body, so
          // it lands here as an undecodable image rather than as a failed response.
          console.error(`${label} WMS response is not an image: ${src}`)
        },
        { once: true },
      )
      element.src = objectUrl
    } catch (error) {
      // Superseded, cancelled or timed out: not a fault worth logging.
      if (signal.aborted) {
        element.src = BLANK_IMAGE_SRC
        return
      }
      console.error(`${label} WMS request failed: ${src}`, error)
      element.src = FAILED_IMAGE_SRC
    }
  }

  const loader = ((image, src) => {
    abortPending()

    const controller = new AbortController()
    const request: PendingRequest = {
      controller,
      timer: setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS),
    }
    pending = request

    void load(
      image.getImage() as HTMLImageElement,
      src,
      controller.signal,
    ).finally(() => {
      clearTimeout(request.timer)
      if (pending === request) pending = null
    })
  }) as AbortingImageLoader

  loader.cancel = abortPending

  return loader
}
